import React, { useMemo, useState } from 'react';
import { Redirect, Link } from 'react-router-dom';
import { Button, Form } from 'react-bootstrap';
import styled from 'styled-components';
import {
  DEV_ROUTES_CATALOG,
  DevRouteEntry,
  buildDevRoutePath,
  isDevUnlockRoutesEnabled,
} from '../../utils/devUnlock';

const Container = styled.div`
  max-width: 960px;
  margin: 24px auto;
  padding: 0 16px 48px;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  margin-bottom: 8px;
`;

const Hint = styled.p`
  color: #555;
  margin-bottom: 24px;
`;

const Card = styled.div`
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  background: #fff;
`;

const Path = styled.code`
  display: block;
  margin: 4px 0 12px;
  word-break: break-all;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 8px;
`;

function RouteCard({ route }: { route: DevRouteEntry }): JSX.Element {
  const [values, setValues] = useState<Record<string, string>>({});
  const href = useMemo(
    () => buildDevRoutePath(route.path, values),
    [route.path, values],
  );

  return (
    <Card>
      <strong>{route.description}</strong>
      <Path>{route.path}</Path>
      {route.params?.map((param) => (
        <Form.Group key={param.name} className="mb-2">
          <Form.Label>{param.label}</Form.Label>
          <Form.Control
            size="sm"
            value={values[param.name] || ''}
            placeholder={param.placeholder || param.name}
            onChange={(event) =>
              setValues((prev) => ({
                ...prev,
                [param.name]: event.target.value,
              }))
            }
          />
        </Form.Group>
      ))}
      <Actions>
        <Link to={href}>
          <Button size="sm" variant="primary">
            Abrir
          </Button>
        </Link>
        <Button
          size="sm"
          variant="outline-secondary"
          type="button"
          onClick={() => navigator.clipboard?.writeText(href)}
        >
          Copiar URL
        </Button>
      </Actions>
    </Card>
  );
}

export default function DevRoutes(): JSX.Element {
  if (!isDevUnlockRoutesEnabled()) {
    return <Redirect to="/" />;
  }

  return (
    <Container>
      <Title>Catálogo de rotas (DEV)</Title>
      <Hint>
        Liberação ativa apenas com <code>REACT_APP_DEV_UNLOCK_ROUTES=true</code>,
        host DEV e cookie <code>DEV_MODE=true</code>. As APIs continuam exigindo
        o token e o perfil reais.
      </Hint>
      {DEV_ROUTES_CATALOG.map((route) => (
        <RouteCard key={route.path} route={route} />
      ))}
    </Container>
  );
}
